import React, { useState, useEffect } from 'react';
import { getAirlineData, getAirportData, getCountryData } from './referenceServices';
import { SuggestAirlineProps, SuggestAirportProps, SuggestCountryProps } from '../../js/headersegment/interfaces';
import '../../styles/modules/waiver-reporting.less';
import '../../styles/modules/trips.less';
import _ from 'lodash';

interface SuggestProps {
  id?: string;
  callback: (item: any) => void;
  className?: string;
  value?: any;
}

interface ValidateIsObjectProps {
  children: React.ReactElement;
  value: any;
  required: boolean;
}

const renderHighlightedText = (text: string, highlight: string) => {
  const regex = new RegExp(`(${highlight})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      <span key={index} className="highlight">
        {part}
      </span>
    ) : (
      part
    )
  );
};

const getFilteredData = (data: any[], filter: string) => {
  return data.filter((item: any) =>
    item.displayName.toLowerCase().includes(filter.toLowerCase())
  );
}


const SuggestAirport: React.FC<SuggestProps> = ({ id, callback, className, value }) => {
  const [airportData, setAirportData] = useState<any[]>([]);
  const [allAirportData, setAllAirportData] = useState<any[]>([]);
  const [airportName, setAirportName] = useState<string>('');
  const [hideDropdown, setHideDropdown] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  useEffect(() => {
    setAirportName(_.isObject(value) && 'displayName' in value ? (value as { displayName: string }).displayName : value || '');
    if (value == '') {
      setAirportData([]);
    }
  }, [value]);

  const onClick = (item: SuggestAirportProps) => {
    setAirportData([]);
    setAirportName(item.displayName);
    callback(item);
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (hideDropdown || airportData.length === 0) return;

    if (event.key === 'ArrowDown') {
      // Navigate down
      setHighlightedIndex((prevIndex) =>
        prevIndex === null || prevIndex === airportData.length - 1
          ? 0
          : prevIndex + 1
      );
    } else if (event.key === 'ArrowUp') {
      // Navigate up
      setHighlightedIndex((prevIndex) =>
        prevIndex === null || prevIndex === 0
          ? airportData.length - 1
          : prevIndex - 1
      );
    } else if ((event.key === 'Enter' || event.key === 'Tab') && highlightedIndex !== null) {
      // Select the highlighted option
      setAirportName(airportData[highlightedIndex].displayName);
      setHideDropdown(true);
      callback(airportData[highlightedIndex]);
      setAirportData([]);
    } else if (event.key === 'Escape') {
      // Close the dropdown
      setHideDropdown(false);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAirportName(e.target.value);
    callback(e.target.value);
    if (e.target.value.length >= 3) {
      if (allAirportData.length) {
        const filteredData = getFilteredData(allAirportData, e.target.value);
        setAirportData(filteredData);
        return;
      }
      getAirportData(e.target.value.substring(0,3)).then(response => {
        const sortData = response.data.sort((a: any, b: any) =>
          a.classification - b.classification ||
          a.city.localeCompare(b.city) ||
          a.name.localeCompare(b.name) ||
          a["airport-code"].localeCompare(b["airport-code"])
        );
        setAllAirportData(sortData);
        const filteredData = getFilteredData(sortData, e.target.value);
        setAirportData(filteredData);
      });
    } else {
      setAllAirportData([]);
      setAirportData([]);
    }
    setHighlightedIndex(0);
  }

  return <>
    <input
      id={id}
      className={`form-control italic-placeholder ${className}`}
      value={airportName}
      type="text"
      autoComplete="off"
      onFocus={() => {
        setHideDropdown(false);
        setHighlightedIndex(0);
      }}
      onKeyDown={handleKeyDown}
      onBlur={() => setTimeout(() => setHideDropdown(true), 200)}
      placeholder="Airport Name or Code"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e)}
    />
    {!hideDropdown && airportData.length ?
      <ul className="dropdown-menu dropdown-menu-auto-suggest">
        {airportData.map((item: SuggestAirportProps, index: number) => (
          <li key={index} className={`batch-option ${highlightedIndex === index ? 'highlighted' : ''
            }`}
            onMouseEnter={() => setHighlightedIndex(index)}
          >
            <a onClick={() => onClick(item)}>{renderHighlightedText(item.displayName, airportName)}</a>
          </li>
        ))}
      </ul> : <></>}
  </>;
};

const SuggestAirline: React.FC<SuggestProps> = ({ callback, className, value }) => {
  const [airlineData, setAirlineData] = useState<any[]>([]);
  const [allAirlineData, setAllAirlineData] = useState<any[]>([]);
  const [airlineName, setAirlineName] = useState<string>('');
  const [hideDropdown, setHideDropdown] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  useEffect(() => {
    setAirlineName(_.isObject(value) && 'displayName' in value ? (value as { displayName: string }).displayName : value || '');
    if (value == '') {
      setAirlineData([]);
    }
  }, [value]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAirlineName(e.target.value);
    callback(e.target.value);
    if (e.target.value.length >= 2) {
      if (allAirlineData.length) {
        const filteredData = getFilteredData(allAirlineData, e.target.value);
        setAirlineData(filteredData);
        return;
      }
      getAirlineData(e.target.value.substring(0,2)).then(response => {
        const sortData = response.data.sort((a: any, b: any) => {
          return a.classification - b.classification || a.name.localeCompare(b.name) || a["airline-code"].localeCompare(b["airline-code"]);
        });
        setAllAirlineData(sortData);
        const filteredData = getFilteredData(sortData, e.target.value);
        setAirlineData(filteredData);
      });
    } else {
      setAllAirlineData([]);
      setAirlineData([]);
    }
    setHighlightedIndex(0);
  }

  const onClick = (item: SuggestAirlineProps) => {
    setAirlineData([]);
    setAirlineName(item.displayName);
    callback(item);
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (hideDropdown || airlineData.length === 0) return;

    if (event.key === 'ArrowDown') {
      // Navigate down
      setHighlightedIndex((prevIndex) =>
        prevIndex === null || prevIndex === airlineData.length - 1
          ? 0
          : prevIndex + 1
      );
    } else if (event.key === 'ArrowUp') {
      // Navigate up
      setHighlightedIndex((prevIndex) =>
        prevIndex === null || prevIndex === 0
          ? airlineData.length - 1
          : prevIndex - 1
      );
    } else if ((event.key === 'Enter' || event.key === 'Tab') && highlightedIndex !== null) {
      // Select the highlighted option
      setAirlineName(airlineData[highlightedIndex].displayName);
      setHideDropdown(true);
      callback(airlineData[highlightedIndex]);
      setAirlineData([]);
    } else if (event.key === 'Escape') {
      // Close the dropdown
      setHideDropdown(true);
    }
  };

  return <>
    <input
      id="airline"
      className={`form-control italic-placeholder ${className}`}
      autoComplete="off"
      type="text"
      placeholder="Airline Name or Code"
      value={airlineName}
      onFocus={() => {
        setHideDropdown(false);
        setHighlightedIndex(0);
      }}
      onKeyDown={handleKeyDown}
      onBlur={() => setTimeout(() => setHideDropdown(true), 200)}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e)} />
    {!hideDropdown && airlineData.length ?
      <ul className="dropdown-menu dropdown-menu-auto-suggest">
        {airlineData.map((item: SuggestAirlineProps, index: number) => (
          <li key={index} className={`batch-option ${highlightedIndex === index ? 'highlighted' : ''
            }`}
            onMouseEnter={() => setHighlightedIndex(index)}>
            <a onClick={() => onClick(item)}>
              {renderHighlightedText(item.displayName, airlineName)}
            </a>
          </li>
        ))}
      </ul> : <></>}
  </>;
};

const SuggestCountry: React.FC<SuggestProps> = ({ callback, className, value }) => {
  const [countryData, setCountryData] = useState<any[]>([]);
  const [allCountryData, setAllCountryData] = useState<any[]>([]);
  const [countryName, setCountryName] = useState<string>('');
  const [hideDropdown, setHideDropdown] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  useEffect(() => {
    setCountryName(_.isObject(value) && 'displayName' in value ? (value as { displayName: string }).displayName : value || '');
    if (value == '') {
      setCountryData([]);
    }
  }, [value]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCountryName(e.target.value);
    if (e.target.value.length >= 2) {
      if (allCountryData.length) {
        const filteredData = getFilteredData(allCountryData, e.target.value);
        setCountryData(filteredData);
        return;
      }
      getCountryData(e.target.value.substring(0,2)).then(response => {
        const sortedData = response.data.sort((a: any, b: any) =>
          a.name.localeCompare(b.name) || a.code.localeCompare(b.code)
        );
        setAllCountryData(sortedData);
        const filteredData = getFilteredData(sortedData, e.target.value);
        setCountryData(filteredData);
      });
    } else {
      setAllCountryData([]);
      setCountryData([]);
    }
    setHighlightedIndex(0);
  }

  const onClick = (item: SuggestCountryProps) => {
    setCountryData([]);
    setCountryName('');
    callback(item);
  }

  
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (hideDropdown || countryData.length === 0) return;

    if (event.key === 'ArrowDown') {
      // Navigate down
      setHighlightedIndex((prevIndex) =>
        prevIndex === null || prevIndex === countryData.length - 1
          ? 0
          : prevIndex + 1
      );
    } else if (event.key === 'ArrowUp') {
      // Navigate up
      setHighlightedIndex((prevIndex) =>
        prevIndex === null || prevIndex === 0
          ? countryData.length - 1
          : prevIndex - 1
      );
    } else if ((event.key === 'Enter' || event.key === 'Tab') && highlightedIndex !== null) {
      // Select the highlighted option
      setCountryName('');
      setHideDropdown(true);
      callback(countryData[highlightedIndex]);
      setCountryData([]);
    } else if (event.key === 'Escape') {
      // Close the dropdown
      setHideDropdown(true);
    }
  };

  return <>
    <input
      className={`form-control section-item m-0 ${className}`}
      type="text"
      placeholder="Type Country Name or Code"
      value={countryName}
      onFocus={() => {
        setHideDropdown(false);
        setHighlightedIndex(0);
      }}
      onKeyDown={handleKeyDown}
      onBlur={() => setTimeout(() => setHideDropdown(true), 200)}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e)}
    />
    {countryData.length ?
      <ul className="dropdown-menu dropdown-menu-auto-suggest" style={{ display: 'block', position: 'relative' }}>
        {countryData.map((item: SuggestCountryProps, index: number) => (
          <li key={index} className={`batch-option ${highlightedIndex === index ? 'highlighted' : ''
          }`}
          onMouseEnter={() => setHighlightedIndex(index)}>
            <a onClick={() => onClick(item)}>{renderHighlightedText(item.displayName, countryName)}</a>
          </li>
        ))}
      </ul> : <></>}
  </>;
};

const ValidateIsObject: React.FC<ValidateIsObjectProps> = ({ children, value, required }) => {
  const [isValid, setIsValid] = useState<boolean>(true);

  useEffect(() => {
    const validate = () => {
      const isObject = value instanceof Object;
      const isNotEmpty = Boolean(value);
      return required ? isObject : isNotEmpty ? isObject : true;
    };

    setIsValid(validate());
  }, [value, required]);

  return React.cloneElement(children, { isValid });
};

export { SuggestAirport, SuggestAirline, SuggestCountry, ValidateIsObject };